import { useEffect, useMemo, useState } from 'react'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'
import {
  getTreasuryActivity,
  getTreasurySummary,
  refreshTreasuryTxs,
  refreshTreasuryTx,
  provisionMissingWallets,
  getHouseReceive,
  sendFromTreasury,
  importHouseMnemonic,
  createHouseWallet,
  updateTreasuryPolicy,
  runTreasuryReconciliation,
  refillSellHot,
  type TreasuryActivityItem,
  type TreasurySummary,
} from '../../api/treasury'
import { createSmkRotationPlan, getSmkStatus, rewrapSmk, verifySmk, type SmkRotationPlan, type SmkStatus } from '../../api/treasurySmk'

const PLANCK_PER_PIXE = 1_000_000_000_000
const planckToPixe = (val?: number | string | null) => {
  if (val === null || val === undefined) return null
  const n = typeof val === 'string' ? Number(val) : val
  if (!Number.isFinite(n)) return null
  return n / PLANCK_PER_PIXE
}

const formatPixe = (val?: number | string | null, fallback = '—') => {
  const n = typeof val === 'string' ? Number(val) : val
  if (!Number.isFinite(n)) return fallback
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 4 })} PIXE`
}

const formatPixeWhole = (val?: number | string | null, fallback = '—') => {
  const n = typeof val === 'string' ? Number(val) : val
  if (!Number.isFinite(n)) return fallback
  return `${Math.trunc(n).toLocaleString()} PIXE`
}

const renderPixeCell = (val?: string | null, fallback = '—') => {
  if (val === null || val === undefined) return fallback
  return `${val} PIXE`
}

const reasonLabels: Record<string, string> = {
  policy_disabled: 'Disabled by policy',
  circuit_tripped: 'Circuit breaker tripped',
  missing_address: 'Missing address',
  missing_signing_key: 'Missing signing key',
  insufficient_available: 'Insufficient available balance',
  below_min_balance: 'Below minimum treasury balance',
  over_per_tx_limit: 'Over per-tx limit',
  over_daily_limit: 'Daily limit exhausted',
  vault_account_restricted: 'Vault account restricted',
}

const refillErrorMessages: Record<string, string> = {
  amount_required: 'Amount required',
  invalid_amount: 'Invalid amount',
  confirm_phrase_mismatch: 'Confirm phrase mismatch',
  circuit_tripped: 'Circuit breaker tripped',
  vault_moves_disabled: 'Vault moves disabled',
  sale_pool_not_configured: 'Pixeloop Reserve not configured',
  sell_hot_not_configured: 'Distribution Account not configured',
  vault_key_required: 'Vault mnemonic required',
  insufficient_available: 'Insufficient available balance',
  insufficient_ledger_balance: 'Insufficient ledger balance',
  insufficient_chain_balance: 'Insufficient chain balance',
  invalid_mnemonic: 'Invalid mnemonic',
  mnemonic_address_mismatch: 'Mnemonic does not match Sale Pool address',
  nonce_error: 'Nonce error',
  invalid_transaction: 'Invalid transaction',
  bad_signature: 'Bad signature',
  payment_error: 'Payment error',
  insufficient_funds: 'Insufficient funds',
  rpc_error: 'RPC error',
  rpc_not_configured: 'RPC not configured',
  rpc_submit_failed: 'RPC submit failed',
  signer_error: 'Signing failed',
  account_not_supported: 'Account not supported',
  broadcast_pending: 'Broadcast pending',
  not_in_chain: 'Not found on-chain',
  submit_missing_hash: 'Missing submit hash',
}

const formatRefillError = (err: any) => {
  const data = err?.response?.data || {}
  const code = data.code
  const traceId = data.traceId
  const base = (code && refillErrorMessages[code]) || data.detail || 'Refill failed'
  return traceId ? `${base} (trace ${traceId.slice(0, 8)})` : base
}

const formatTxFailure = (code?: string | null) => {
  if (!code) return null
  return refillErrorMessages[code] || code
}

const formatSs58Short = (addr?: string | null) => {
  if (!addr) return 'Not configured'
  if (addr.length <= 12) return addr
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`
}

const copyToClipboard = (value?: string | null) => {
  if (!value) return
  navigator.clipboard?.writeText(value)
}

const HOUSE_LABELS: Record<string, string> = {
  sale_pool: 'Pixeloop Reserve',
  sell_hot: 'Distribution Account',
  fees: 'Fees',
}

const displayAccountLabel = (key: string, fallback: string) => {
  return HOUSE_LABELS[key] || fallback || key
}

const formatActivityParty = (value: string, houseAccounts?: TreasuryHouseAccount[]) => {
  if (!value) return value
  const byKey = HOUSE_LABELS[value]
  if (byKey) return byKey
  const byAddress = houseAccounts?.find((h) => h.ss58 && h.ss58 === value)
  if (byAddress) return displayAccountLabel(byAddress.key, byAddress.label)
  return value
}

const revealPhraseFor = (key: string) => (key === 'fees' ? 'REVEAL FEES' : 'REVEAL SELL HOT')

const VISIBLE_HOUSE_KEYS = ['sale_pool', 'sell_hot', 'fees']

const parseTreasuryTxId = (id: string | number) => {
  if (typeof id !== 'string') return null
  if (!id.startsWith('ttx-')) return null
  const raw = Number(id.slice(4))
  return Number.isFinite(raw) ? raw : null
}

const shortHash = (h?: string | null) => {
  if (!h) return '—'
  return h.length > 14 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h
}

const normalizeSummary = (sum: TreasurySummary | null): TreasurySummary => {
  if (!sum) return FALLBACK_SUMMARY
  return {
    ...FALLBACK_SUMMARY,
    ...sum,
    chain: { ...FALLBACK_SUMMARY.chain, ...(sum.chain || {}) },
    settings: { ...FALLBACK_SUMMARY.settings, ...(sum.settings || {}) },
    reconciliation: { ...FALLBACK_SUMMARY.reconciliation, ...(sum.reconciliation || {}) },
    houseAccounts: (sum.houseAccounts && sum.houseAccounts.length ? sum.houseAccounts : FALLBACK_SUMMARY.houseAccounts).map((h) => ({
      ...h,
    })),
  }
}

const FALLBACK_SUMMARY: TreasurySummary = {
  chain: {
    name: 'PixeChain Mainnet',
    rpc: 'http://10.124.0.6:9934',
    rpcConfigured: 'http://10.124.0.6:9934',
    rpcEffective: 'http://10.124.0.6:9934',
    error: null,
    ss58: 42,
    healthy: true,
    latestBlock: '0x24b9',
    latestBlockHex: '0x24b9',
    peers: 4,
    isSyncing: false,
  },
  houseAccounts: [
    {
      key: 'sale_pool',
      label: 'Pixeloop Sale Pool',
      ss58: '5DU7BbKY9iFoiJ7nMa8Ppb9gh8TvCtsjFKsaQ8EHg7d8ugCX',
      role: 'watch',
      balancePixe: '0',
      balancePlanck: '0',
      availablePixe: '0',
      heldPixe: '0',
      totalPixe: '0',
      availablePlanck: 0,
      heldPlanck: 0,
      totalPlanck: 0,
      hasKey: false,
      canSend: false,
      balanceSource: 'chain',
      status: 'watch-only',
    },
    {
      key: 'sell_hot',
      label: 'Sell Hot',
      ss58: null,
      role: 'hot',
      balancePixe: null,
      balancePlanck: null,
      availablePixe: null,
      heldPixe: null,
      totalPixe: null,
      availablePlanck: 0,
      heldPlanck: 0,
      totalPlanck: 0,
      hasKey: false,
      canSend: false,
      balanceSource: 'ledger',
      status: 'missing',
    },
    {
      key: 'fees',
      label: 'Fees',
      ss58: null,
      role: 'watch',
      balancePixe: null,
      balancePlanck: null,
      availablePixe: null,
      heldPixe: null,
      totalPixe: null,
      availablePlanck: 0,
      heldPlanck: 0,
      totalPlanck: 0,
      hasKey: false,
      canSend: false,
      balanceSource: 'ledger',
      status: 'missing',
    },
  ],
  settings: {
    sendsEnabled: false,
    vaultMovesEnabled: false,
    perTxLimitPlanck: 500000000000,
    perDayLimitPlanck: 2000000000000,
    dailyRemainingPlanck: 1800000000000,
    minTreasuryBalancePlanck: 1000000000000,
    confirmationsRequired: 2,
    confirmPhrase: 'SEND PIXE',
    circuitTripped: false,
  },
  reconciliation: {
    lastRunAt: '2026-01-11T17:00:00Z',
    mismatches: 0,
    status: 'clean',
    basis: 'spot',
  },
  kpis: { outgoing24h: '0', outgoing7d: '0', pendingOps: '0', walletsProvisionedPct: '0', walletsProvisionedCount: 0, walletsEligibleCount: 0 },
}

const FALLBACK_ACTIVITY: TreasuryActivityItem[] = [
  { id: 'evt_mock_1', ts: '2026-01-11T18:00:00Z', type: 'transfer', from: 'treasury', to: 'user_123', amountPixe: '100', status: 'submitted', txHash: '0xabc', blockNumber: null },
  { id: 'evt_mock_2', ts: '2026-01-11T17:30:00Z', type: 'transfer', from: 'treasury', to: 'user_456', amountPixe: '50', status: 'in_block', txHash: '0xdef', blockNumber: null },
  { id: 'evt_mock_3', ts: '2026-01-11T15:10:00Z', type: 'transfer', from: 'treasury', to: 'user_789', amountPixe: '25', status: 'finalized', txHash: '0xghi', blockNumber: null },
  { id: 'evt_mock_4', ts: '2026-01-11T13:00:00Z', type: 'transfer', from: 'treasury', to: 'user_000', amountPixe: '10', status: 'failed', txHash: '0xjkl', blockNumber: null },
]

function Pill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'success' | 'danger' | 'warning' }) {
  const styles: Record<string, string> = {
    neutral: 'bg-white/5 text-white/80',
    success: 'bg-emerald-900/40 text-emerald-200 border border-emerald-500/40',
    danger: 'bg-rose-900/40 text-rose-200 border border-rose-500/40',
    warning: 'bg-amber-900/40 text-amber-100 border border-amber-500/40',
  }
  return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[tone]}`}>{children}</span>
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-2">
      <div className="text-sm text-white/70">{label}</div>
      <div className="text-2xl font-extrabold text-white">{value}</div>
      {hint && <div className="text-xs text-white/60">{hint}</div>}
    </div>
  )
}

export default function TreasurySettings() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const isRoot = Boolean(me?.is_superuser)

  const [summary, setSummary] = useState<TreasurySummary | null>(null)
  const [activity, setActivity] = useState<TreasuryActivityItem[]>([])
  const [loading, setLoading] = useState(false)
  const [mockMode, setMockMode] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [receiveInfo, setReceiveInfo] = useState<{ key: string; ss58: string | null } | null>(null)
  const [sendState, setSendState] = useState<{ open: boolean; houseKey: string; dest: string; amount: string; memo: string; confirm: string; status?: string }>({
    open: false,
    houseKey: 'sell_hot',
    dest: '',
    amount: '100',
    memo: '',
    confirm: '',
  })
  const [importState, setImportState] = useState<{ houseKey: string; mnemonic: string; status?: string; open: boolean; ackLargeBalanceRisk?: boolean }>({
    houseKey: 'sell_hot',
    mnemonic: '',
    open: false,
    ackLargeBalanceRisk: false,
  })
  const [createState, setCreateState] = useState<{
    houseKey: string
    open: boolean
    revealOnce: boolean
    revealAck: boolean
    confirmPhrase: string
    status?: string
    result?: { ss58: string; mnemonic?: string | null }
  }>({
    houseKey: 'sell_hot',
    open: false,
    revealOnce: false,
    revealAck: false,
    confirmPhrase: '',
  })
  const [policyState, setPolicyState] = useState<{
    open: boolean
    sendsEnabled: boolean
    vaultMovesEnabled: boolean
    perTxLimitPlanck: string
    perDayLimitPlanck: string
    minTreasuryBalancePlanck: string
    confirmationsRequired: string
    confirmPhrase: string
    status?: string
  }>({
    open: false,
    sendsEnabled: false,
    vaultMovesEnabled: false,
    perTxLimitPlanck: '',
    perDayLimitPlanck: '',
    minTreasuryBalancePlanck: '',
    confirmationsRequired: '',
    confirmPhrase: '',
  })
  const [refillState, setRefillState] = useState<{
    open: boolean
    amount: string
    confirm: string
    vaultMnemonic: string
    vaultAck: boolean
    revealMnemonic: boolean
    status?: string
  }>({
    open: false,
    amount: '1000000',
    confirm: '',
    vaultMnemonic: '',
    vaultAck: false,
    revealMnemonic: false,
  })
  const [smkStatus, setSmkStatus] = useState<SmkStatus | null>(null)
  const [smkPlan, setSmkPlan] = useState<SmkRotationPlan | null>(null)
  const [smkMessage, setSmkMessage] = useState<string | null>(null)
  const [smkCopyNotice, setSmkCopyNotice] = useState<string | null>(null)
  const [smkLastChecked, setSmkLastChecked] = useState<string | null>(null)
  const [rewrapConfirm, setRewrapConfirm] = useState('')

  const provisioningPct = useMemo(() => {
    const raw = summary?.kpis.walletsProvisionedPct || '0'
    const n = Number(raw)
    return Number.isFinite(n) ? Math.min(Math.max(n, 0), 100) : 0
  }, [summary])
  const refreshAll = () => {
    if (!authed || !isRoot) return
    setLoading(true)
    refreshTreasuryTxs(50)
      .catch(() => null)
      .then(() => Promise.all([getTreasurySummary(), getTreasuryActivity(50)]))
      .then(([sum, act]) => {
        setSummary(normalizeSummary(sum))
        setActivity(act)
        setMockMode(false)
      })
      .catch(() => {
        setSummary(FALLBACK_SUMMARY)
        setActivity(FALLBACK_ACTIVITY)
        setMockMode(true)
      })
      .finally(() => setLoading(false))
    refreshSmkStatus()
  }

  useEffect(() => {
    if (!authed || !isRoot) return
    refreshAll()
  }, [authed, isRoot])

  if (!authed) {
    return <div className="text-white/80">Please sign in to view Treasury.</div>
  }

  if (!isRoot) {
    return <div className="text-white/80">Treasury is restricted to root users.</div>
  }

  const merged = normalizeSummary(summary)
  const totalManagedPixe = (merged.houseAccounts || []).reduce((acc, h) => {
    const n = Number(h.totalPixe ?? h.balancePixe ?? 0)
    return Number.isFinite(n) ? acc + n : acc
  }, 0)
  const chain = merged.chain
  const settings = merged.settings || FALLBACK_SUMMARY.settings
  const reconciliation = merged.reconciliation || FALLBACK_SUMMARY.reconciliation
  const findHouse = (key: string) => merged.houseAccounts.find((h) => h.key === key)
  const salePool = findHouse('sale_pool')
  const sellHot = findHouse('sell_hot')
  const visibleAccounts = (summary?.houseAccounts || FALLBACK_SUMMARY.houseAccounts)
    .filter((acct) => VISIBLE_HOUSE_KEYS.includes(acct.key))
    .sort((a, b) => VISIBLE_HOUSE_KEYS.indexOf(a.key) - VISIBLE_HOUSE_KEYS.indexOf(b.key))
  const revealPhrase = revealPhraseFor(createState.houseKey)
  const revealReady =
    !createState.revealOnce ||
    (createState.revealAck && createState.confirmPhrase.trim() === revealPhrase)
  const latestBlockDisplay = (chain as any)?.latestBlockHex || (chain as any)?.latestBlock
  const rpcDisplay = (chain as any).rpcEffective || chain.rpc || '—'
  const sendHouse = findHouse(sendState.houseKey)
  const needsVaultMnemonic = Boolean(salePool && !salePool.hasKey)
  const canRefill =
    Boolean(settings?.vaultMovesEnabled) &&
    !settings?.circuitTripped &&
    Boolean(salePool?.ss58) &&
    Boolean(sellHot?.ss58)
  const refillReason = settings?.circuitTripped
    ? 'Circuit breaker tripped'
    : !settings?.vaultMovesEnabled
    ? 'Vault moves disabled'
    : !salePool?.ss58
    ? 'Missing sale pool address'
    : !sellHot?.ss58
    ? 'Missing sell hot address'
    : ''
  const provisioningCount = summary?.kpis.walletsProvisionedCount ?? 0
  const provisioningEligible = summary?.kpis.walletsEligibleCount ?? 0
  const perTxLimitPixe = planckToPixe(settings?.perTxLimitPlanck) ?? planckToPixe(FALLBACK_SUMMARY.settings?.perTxLimitPlanck)
  const perDayLimitPixe = planckToPixe(settings?.perDayLimitPlanck) ?? planckToPixe(FALLBACK_SUMMARY.settings?.perDayLimitPlanck)
  const dailyRemainingPixe = planckToPixe(settings?.dailyRemainingPlanck) ?? planckToPixe(FALLBACK_SUMMARY.settings?.dailyRemainingPlanck)
  const minTreasuryBalancePixe = planckToPixe(settings?.minTreasuryBalancePlanck) ?? planckToPixe(FALLBACK_SUMMARY.settings?.minTreasuryBalancePlanck)

  const openPolicyEditor = () => {
    setPolicyState({
      open: true,
      sendsEnabled: Boolean(settings?.sendsEnabled),
      vaultMovesEnabled: Boolean(settings?.vaultMovesEnabled),
      perTxLimitPlanck: settings?.perTxLimitPlanck?.toString() || '0',
      perDayLimitPlanck: settings?.perDayLimitPlanck?.toString() || '0',
      minTreasuryBalancePlanck: settings?.minTreasuryBalancePlanck?.toString() || '0',
      confirmationsRequired: settings?.confirmationsRequired?.toString() || '1',
      confirmPhrase: settings?.confirmPhrase || '',
    })
  }
  const modalReasons = sendHouse?.sendDisabledReasons || []
  const sendDisabled =
    settings?.sendsEnabled === false ||
    modalReasons.length > 0 ||
    (settings?.confirmPhrase && sendState.confirm.trim().toUpperCase() !== settings.confirmPhrase.toUpperCase()) ||
    !sendHouse?.ss58
  const reconTone = reconciliation?.status === 'failed' ? 'danger' : reconciliation?.mismatches ? 'warning' : 'success'

  const doReceive = (houseKey: string) => {
    setMessage(null)
    getHouseReceive(houseKey)
      .then((info) => setReceiveInfo(info))
      .catch(() => setReceiveInfo({ key: houseKey, ss58: null }))
  }

  const doSend = () => {
    if (!sendState.dest || !sendState.amount) return
    setSendState((s) => ({ ...s, status: 'Submitting…' }))
    sendFromTreasury({
      from_house_key: sendState.houseKey,
      dest_ss58: sendState.dest,
      amount: sendState.amount,
      planck: false,
      memo: sendState.memo || undefined,
    })
      .then((res) => {
        setSendState((s) => ({ ...s, status: `Submitted ${res.tx_hash || ''}`, open: false }))
        refreshAll()
        getTreasuryActivity(50).then(setActivity).catch(() => setActivity(FALLBACK_ACTIVITY))
      })
      .catch((err) => {
        setSendState((s) => ({ ...s, status: err?.response?.data?.detail || 'Send failed' }))
      })
  }

  const notifySmkCopy = (label: string) => {
    setSmkCopyNotice(`${label} copied`)
    window.setTimeout(() => setSmkCopyNotice(null), 2000)
  }

  const refreshSmkStatus = () => {
    return getSmkStatus()
      .then((smk) => {
        setSmkStatus(smk)
        setSmkLastChecked(new Date().toISOString())
      })
      .catch(() => {
        setSmkStatus(null)
        setSmkLastChecked(new Date().toISOString())
      })
  }

  return (
    <div className="space-y-5">
      {mockMode && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-900/30 p-3 text-amber-50">
          Mock data — backend wiring pending or temporarily unavailable.
        </div>
      )}
      {settings && settings.sendsEnabled === false && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-900/40 p-3 text-amber-100">
          Sends are currently disabled by policy. Toggle sends_enabled when ready.
        </div>
      )}
      {message && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-white/80">
          {message}
        </div>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white">Treasury</h1>
          <p className="text-white/70">Root-only operations console for Pixeloop custody + house accounts.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshAll}
            className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
            disabled={loading}
          >
            Refresh
          </button>
          <button className="px-4 py-2 rounded-lg bg-white/5 text-white/60 cursor-not-allowed" disabled>
            Export report
          </button>
        </div>
      </div>

      {receiveInfo && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">Receive: {displayAccountLabel(receiveInfo.key, receiveInfo.key)}</div>
              <button className="text-white/70" onClick={() => setReceiveInfo(null)}>
                ✕
              </button>
            </div>
            <div className="text-sm text-white/70">Share this address to receive PIXE.</div>
            <div className="font-mono text-white break-all bg-white/5 rounded-lg p-3">
              {receiveInfo.ss58 || 'Not configured'}
            </div>
            <button
              className="w-full px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500"
              onClick={() => {
                if (receiveInfo.ss58) navigator.clipboard?.writeText(receiveInfo.ss58)
              }}
            >
              Copy address
            </button>
          </div>
        </div>
      )}

      {sendState.open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">
                Send from {displayAccountLabel(sendState.houseKey, sendState.houseKey)}
              </div>
              <button className="text-white/70" onClick={() => setSendState((s) => ({ ...s, open: false }))}>
                ✕
              </button>
            </div>
            <div className="text-sm text-white/70">
              Available: {formatPixe(findHouse(sendState.houseKey)?.availablePixe || findHouse(sendState.houseKey)?.balancePixe)} · Held:{' '}
              {formatPixe(findHouse(sendState.houseKey)?.heldPixe)} · Total: {formatPixe(findHouse(sendState.houseKey)?.totalPixe || findHouse(sendState.houseKey)?.balancePixe)}
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70">Destination SS58</label>
              <input
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white"
                value={sendState.dest}
                onChange={(e) => setSendState((s) => ({ ...s, dest: e.target.value }))}
                placeholder="5..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70">Amount (PIXE)</label>
              <input
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white"
                value={sendState.amount}
                onChange={(e) => setSendState((s) => ({ ...s, amount: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70">Memo (optional)</label>
              <input
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white"
                value={sendState.memo}
                onChange={(e) => setSendState((s) => ({ ...s, memo: e.target.value }))}
                placeholder="Ops note"
              />
            </div>
            {settings?.confirmPhrase && (
              <div className="space-y-2">
                <label className="text-sm text-white/70">Type confirmation phrase ({settings.confirmPhrase})</label>
                <input
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white"
                  value={sendState.confirm}
                  onChange={(e) => setSendState((s) => ({ ...s, confirm: e.target.value }))}
                  placeholder={settings.confirmPhrase}
                />
              </div>
            )}
            {sendState.status && <div className="text-xs text-white/70">{sendState.status}</div>}
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
                onClick={() => setSendState((s) => ({ ...s, open: false }))}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-50"
                onClick={doSend}
                disabled={sendDisabled}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {refillState.open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">Refill sell hot</div>
              <button
                className="text-white/70"
                onClick={() => setRefillState((s) => ({ ...s, open: false, vaultMnemonic: '', vaultAck: false, revealMnemonic: false }))}
              >
                ƒo
              </button>
            </div>
            <div className="text-sm text-white/70">
              Moves PIXE from sale pool to sell hot (requires vault moves enabled).
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70">Amount (PIXE)</label>
              <input
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white"
                value={refillState.amount}
                onChange={(e) => setRefillState((s) => ({ ...s, amount: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70">Type confirmation phrase (REFILL {refillState.amount})</label>
              <input
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white"
                value={refillState.confirm}
                onChange={(e) => setRefillState((s) => ({ ...s, confirm: e.target.value }))}
                placeholder={`REFILL ${refillState.amount}`}
              />
            </div>
            {needsVaultMnemonic && (
              <div className="space-y-2 rounded-lg border border-rose-500/30 bg-rose-900/20 p-3">
                <div className="text-xs text-rose-200">Sale Pool key is not stored. Paste the vault mnemonic to sign this refill only.</div>
                <label className="flex items-center gap-2 text-xs text-white/80">
                  <input
                    type="checkbox"
                    checked={refillState.vaultAck}
                    onChange={(e) => setRefillState((s) => ({ ...s, vaultAck: e.target.checked }))}
                  />
                  I understand this is the Sale Pool vault key
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type={refillState.revealMnemonic ? 'text' : 'password'}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white text-xs"
                    value={refillState.vaultMnemonic}
                    onChange={(e) => setRefillState((s) => ({ ...s, vaultMnemonic: e.target.value }))}
                    placeholder="Vault mnemonic (not stored)"
                  />
                  <button
                    className="px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20 text-xs"
                    onClick={() => setRefillState((s) => ({ ...s, revealMnemonic: !s.revealMnemonic }))}
                  >
                    {refillState.revealMnemonic ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            )}
            {refillState.status && <div className="text-xs text-white/70">{refillState.status}</div>}
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
                onClick={() => setRefillState((s) => ({ ...s, open: false, vaultMnemonic: '', vaultAck: false, revealMnemonic: false }))}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-amber-600 text-black hover:bg-amber-500 disabled:opacity-50"
                onClick={() => {
                  const expected = `REFILL ${refillState.amount}`
                  if (refillState.confirm.trim() !== expected) {
                    setRefillState((s) => ({ ...s, status: 'Confirm phrase mismatch' }))
                    return
                  }
                  if (needsVaultMnemonic && !refillState.vaultMnemonic.trim()) {
                    setRefillState((s) => ({ ...s, status: 'Vault mnemonic required' }))
                    return
                  }
                  refillSellHot({
                    amount: refillState.amount,
                    planck: false,
                    confirmPhrase: refillState.confirm,
                    vaultMnemonic: needsVaultMnemonic ? refillState.vaultMnemonic.trim() : undefined,
                  })
                    .then((res) => {
                      setRefillState((s) => ({
                        ...s,
                        status: `Submitted ${res.tx_hash || ''}`,
                        open: false,
                        vaultMnemonic: '',
                        vaultAck: false,
                        revealMnemonic: false,
                      }))
                      refreshAll()
                    })
                    .catch((err) => setRefillState((s) => ({ ...s, status: formatRefillError(err) })))
                  }}
                disabled={!canRefill || (needsVaultMnemonic && (!refillState.vaultAck || !refillState.vaultMnemonic.trim()))}
                title={!canRefill ? refillReason : needsVaultMnemonic && !refillState.vaultAck ? 'Vault key required' : ''}
              >
                Refill
              </button>
            </div>
          </div>
        </div>
      )}

      {smkPlan && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">SMK rotation plan</div>
              <button className="text-white/70 hover:text-white" onClick={() => setSmkPlan(null)}>
                Close
              </button>
            </div>
            <div className="rounded-lg border border-rose-500/40 bg-rose-900/30 p-3 text-sm text-rose-100">
              Treat this as a master key; store offline and never paste into chat.
            </div>
            <div className="text-sm text-white/70">Apply the snippet in your cluster.</div>
            <div className="bg-black/40 rounded-lg p-3 font-mono text-xs text-white whitespace-pre-wrap">
              {smkPlan.kubectlPatchSnippet}
            </div>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20"
                onClick={() =>
                  navigator.clipboard
                    ?.writeText(smkPlan.kubectlPatchSnippet)
                    .then(() => notifySmkCopy('Snippet'))
                    .catch(() => setSmkCopyNotice('Copy failed'))
                }
              >
                Copy snippet
              </button>
              <button className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20" onClick={() => setSmkPlan(null)}>
                Close
              </button>
            </div>
            {smkCopyNotice && <div className="text-xs text-emerald-200">{smkCopyNotice}</div>}
            <div className="text-xs text-white/70 space-y-1">
              {smkPlan.notes.map((note, idx) => (
                <div key={idx}>- {note}</div>
              ))}
            </div>
            <div className="flex justify-end">
              <button className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20" onClick={() => setSmkPlan(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {importState.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">
                Import key for {displayAccountLabel(importState.houseKey, importState.houseKey)}
              </div>
              <button className="text-white/70" onClick={() => setImportState({ houseKey: 'sell_hot', mnemonic: '', open: false, ackLargeBalanceRisk: false })}>
                ✕
              </button>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70">Mnemonic</label>
              <textarea
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white"
                rows={3}
                value={importState.mnemonic}
                onChange={(e) => setImportState((s) => ({ ...s, mnemonic: e.target.value }))}
                placeholder="seed phrase"
              />
            </div>
            {importState.houseKey === 'sale_pool' && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-900/30 p-3 text-xs text-amber-100 space-y-2">
                <div>Sale pool controls a large balance. Confirm you understand the risk before importing.</div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(importState.ackLargeBalanceRisk)}
                    onChange={(e) => setImportState((s) => ({ ...s, ackLargeBalanceRisk: e.target.checked }))}
                  />
                  <span>I acknowledge the sale pool risk</span>
                </label>
              </div>
            )}
            {importState.status && <div className="text-xs text-white/70">{importState.status}</div>}
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20" onClick={() => setImportState({ houseKey: 'sell_hot', mnemonic: '', open: false, ackLargeBalanceRisk: false })}>
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-amber-600 text-black hover:bg-amber-500"
                onClick={() => {
                  importHouseMnemonic(importState.houseKey, importState.mnemonic, {
                    ackLargeBalanceRisk: Boolean(importState.ackLargeBalanceRisk),
                  })
                    .then((res) => {
                      setImportState({ houseKey: importState.houseKey, mnemonic: '', status: `Imported ${res.ss58}`, open: false, ackLargeBalanceRisk: false })
                      getTreasurySummary().then(setSummary).catch(() => setSummary(FALLBACK_SUMMARY))
                    })
                    .catch((err) => setImportState((s) => ({ ...s, status: err?.response?.data?.detail || 'Import failed' })))
                }}
                disabled={importState.houseKey === 'sale_pool' && !importState.ackLargeBalanceRisk}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {createState.open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">
                Create wallet for {displayAccountLabel(createState.houseKey, createState.houseKey)}
              </div>
              <button
                className="text-white/70"
                onClick={() =>
                  setCreateState({
                    houseKey: createState.houseKey,
                    open: false,
                    revealOnce: false,
                    revealAck: false,
                    confirmPhrase: '',
                  })
                }
              >
                ×
              </button>
            </div>
            <div className="text-sm text-white/70">This creates a custody wallet stored encrypted under the SMK.</div>

            {createState.result ? (
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-white/60">SS58 address</div>
                  <div className="font-mono text-white break-all bg-white/5 rounded-lg p-3">{createState.result.ss58}</div>
                  <button
                    className="mt-2 px-3 py-1 rounded bg-white/10 text-white hover:bg-white/20 text-xs"
                    onClick={() => copyToClipboard(createState.result?.ss58)}
                  >
                    Copy address
                  </button>
                </div>
                {createState.result.mnemonic && (
                  <div>
                    <div className="text-xs text-rose-200">Displayed once. Store offline now.</div>
                    <div className="font-mono text-white break-all bg-rose-900/30 border border-rose-500/40 rounded-lg p-3">
                      {createState.result.mnemonic}
                    </div>
                    <button
                      className="mt-2 px-3 py-1 rounded bg-white/10 text-white hover:bg-white/20 text-xs"
                      onClick={() => copyToClipboard(createState.result?.mnemonic || '')}
                    >
                      Copy mnemonic
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input
                    type="checkbox"
                    checked={createState.revealOnce}
                    onChange={(e) => setCreateState((s) => ({ ...s, revealOnce: e.target.checked }))}
                  />
                  Reveal mnemonic (advanced)
                </label>
                {createState.revealOnce && (
                  <div className="space-y-2 rounded-lg border border-rose-500/30 bg-rose-900/20 p-3">
                    <div className="text-xs text-rose-200">This will be displayed once. Store offline.</div>
                    <label className="flex items-center gap-2 text-xs text-white/80">
                      <input
                        type="checkbox"
                        checked={createState.revealAck}
                        onChange={(e) => setCreateState((s) => ({ ...s, revealAck: e.target.checked }))}
                      />
                      I will store this offline
                    </label>
                    <input
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white text-xs"
                      placeholder={revealPhrase}
                      value={createState.confirmPhrase}
                      onChange={(e) => setCreateState((s) => ({ ...s, confirmPhrase: e.target.value }))}
                    />
                    {!createState.revealAck && (
                      <div className="text-[11px] text-rose-200">Check the offline box to enable reveal.</div>
                    )}
                    {createState.revealAck && createState.confirmPhrase.trim() !== revealPhrase && (
                      <div className="text-[11px] text-rose-200">Type exactly: {revealPhrase}</div>
                    )}
                  </div>
                )}
              </>
            )}

            {createState.status && <div className="text-xs text-white/70">{createState.status}</div>}
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
                onClick={() =>
                  setCreateState({
                    houseKey: createState.houseKey,
                    open: false,
                    revealOnce: false,
                    revealAck: false,
                    confirmPhrase: '',
                  })
                }
              >
                Close
              </button>
              {!createState.result && (
                <button
                  className="px-4 py-2 rounded-lg bg-amber-600 text-black hover:bg-amber-500 disabled:opacity-50"
                  disabled={!revealReady}
                  title={revealReady ? '' : 'Confirm offline + exact phrase to reveal'}
                  onClick={() => {
                    setCreateState((s) => ({ ...s, status: 'Creating...' }))
                    createHouseWallet(createState.houseKey, {
                      revealOnce: createState.revealOnce,
                      confirmPhrase: createState.revealOnce ? createState.confirmPhrase.trim() : undefined,
                    })
                      .then((res) => {
                        setCreateState((s) => ({
                          ...s,
                          status: 'Wallet created',
                          result: { ss58: res.ss58, mnemonic: res.mnemonic ?? null },
                        }))
                        refreshAll()
                      })
                      .catch((err) => {
                        const detail = err?.response?.data?.detail || 'Create failed'
                        setCreateState((s) => ({ ...s, status: detail }))
                      })
                  }}
                >
                  Create wallet
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {policyState.open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-white">Edit treasury policy</div>
              <button className="text-white/70" onClick={() => setPolicyState((s) => ({ ...s, open: false }))}>
                ×
              </button>
            </div>

            <div className="space-y-3">
              <label className="flex items-center justify-between text-sm text-white/80">
                <span>Vault moves enabled</span>
                <input
                  type="checkbox"
                  checked={policyState.vaultMovesEnabled}
                  onChange={(e) => setPolicyState((s) => ({ ...s, vaultMovesEnabled: e.target.checked }))}
                />
              </label>
              <label className="flex items-center justify-between text-sm text-white/80">
                <span>Sends enabled</span>
                <input
                  type="checkbox"
                  checked={policyState.sendsEnabled}
                  onChange={(e) => setPolicyState((s) => ({ ...s, sendsEnabled: e.target.checked }))}
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-white/60">Per-tx limit (planck)</div>
                <input
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white text-xs"
                  value={policyState.perTxLimitPlanck}
                  onChange={(e) => setPolicyState((s) => ({ ...s, perTxLimitPlanck: e.target.value }))}
                />
              </div>
              <div>
                <div className="text-white/60">Per-day limit (planck)</div>
                <input
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white text-xs"
                  value={policyState.perDayLimitPlanck}
                  onChange={(e) => setPolicyState((s) => ({ ...s, perDayLimitPlanck: e.target.value }))}
                />
              </div>
              <div>
                <div className="text-white/60">Min treasury balance (planck)</div>
                <input
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white text-xs"
                  value={policyState.minTreasuryBalancePlanck}
                  onChange={(e) => setPolicyState((s) => ({ ...s, minTreasuryBalancePlanck: e.target.value }))}
                />
              </div>
              <div>
                <div className="text-white/60">Confirmations</div>
                <input
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white text-xs"
                  value={policyState.confirmationsRequired}
                  onChange={(e) => setPolicyState((s) => ({ ...s, confirmationsRequired: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <div className="text-white/60 text-sm">Confirm phrase</div>
              <input
                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white text-xs"
                value={policyState.confirmPhrase}
                onChange={(e) => setPolicyState((s) => ({ ...s, confirmPhrase: e.target.value }))}
              />
            </div>

            {policyState.status && <div className="text-xs text-white/70">{policyState.status}</div>}

            <div className="flex justify-end gap-2">
              <button className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20" onClick={() => setPolicyState((s) => ({ ...s, open: false }))}>
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500"
                onClick={() => {
                  const perTx = Number(policyState.perTxLimitPlanck)
                  const perDay = Number(policyState.perDayLimitPlanck)
                  const minBal = Number(policyState.minTreasuryBalancePlanck)
                  const confirmations = Number(policyState.confirmationsRequired)
                  if ([perTx, perDay, minBal, confirmations].some((n) => !Number.isFinite(n) || n < 0)) {
                    setPolicyState((s) => ({ ...s, status: 'Invalid numeric values' }))
                    return
                  }
                  setPolicyState((s) => ({ ...s, status: 'Saving...' }))
                  updateTreasuryPolicy({
                    vaultMovesEnabled: policyState.vaultMovesEnabled,
                    sendsEnabled: policyState.sendsEnabled,
                    perTxLimitPlanck: perTx,
                    perDayLimitPlanck: perDay,
                    minTreasuryBalancePlanck: minBal,
                    confirmationsRequired: Math.max(1, Math.floor(confirmations)),
                    confirmPhrase: policyState.confirmPhrase,
                  })
                    .then(() => {
                      setPolicyState((s) => ({ ...s, status: 'Policy updated', open: false }))
                      refreshAll()
                    })
                    .catch((err) => {
                      const detail = err?.response?.data?.detail || 'Update failed'
                      setPolicyState((s) => ({ ...s, status: detail }))
                    })
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Total managed" value={formatPixeWhole(totalManagedPixe || 0)} hint="Sum of house balances" />
        <StatCard label="Outgoing 24h" value={`${summary?.kpis.outgoing24h || '0'} PIXE`} />
        <StatCard label="Pending ops" value={summary?.kpis.pendingOps || '0'} />
        <StatCard label="Wallet provisioning" value={`${provisioningPct}%`} hint={`${provisioningCount} / ${provisioningEligible} provisioned`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-white/70">Chain Health</div>
              <div className="text-xl font-bold text-white">{chain.name}</div>
            </div>
            <Pill tone={chain.healthy ? 'success' : 'danger'}>{chain.healthy ? 'Healthy' : 'Unhealthy'}</Pill>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm text-white/80">
            <div>
              <div className="text-white/60">RPC</div>
              <div className="font-mono break-all" title={(chain as any).rpcConfigured || ''}>
                {rpcDisplay}
              </div>
            </div>
            <div>
              <div className="text-white/60">Latest block</div>
              <div className="font-mono">{latestBlockDisplay ?? '—'}</div>
            </div>
            <div>
              <div className="text-white/60">Peers</div>
              <div>{chain.peers ?? '—'}</div>
            </div>
            <div>
              <div className="text-white/60">SS58</div>
              <div>{chain.ss58}</div>
            </div>
            <div>
              <div className="text-white/60">Syncing</div>
              <div>{chain.isSyncing === undefined || chain.isSyncing === null ? '—' : chain.isSyncing ? 'Yes' : 'No'}</div>
            </div>
            {(chain as any)?.error && (
              <div className="col-span-2 text-rose-200 text-xs font-mono">Error: {(chain as any).error}</div>
            )}
          </dl>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-white/70">Wallet provisioning</div>
              <div className="text-xl font-bold text-white">User custody readiness</div>
              <div className="text-sm text-white/60">Custodial wallets are provisioned automatically.</div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-brand" style={{ width: `${provisioningPct}%` }} />
          </div>
          <div className="flex items-center justify-between text-sm text-white/70">
            <span>
              {provisioningPct}% provisioned · {provisioningCount} / {provisioningEligible} wallets
            </span>
            <button
              className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20"
              onClick={() => {
                provisionMissingWallets()
                  .then(() => {
                    setMessage('Provisioning kicked off for your account')
                    refreshAll()
                  })
                  .catch(() => setMessage('Provisioning failed; check server logs'))
              }}
            >
              Provision missing wallets
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-white/70">Limits & guardrails</div>
              <div className="text-xl font-bold text-white">Sends {settings?.sendsEnabled ? 'enabled' : 'disabled'}</div>
              {!settings?.sendsEnabled && <div className="text-sm text-amber-300">Sends are off (toggle in settings).</div>}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 rounded-lg bg-white/10 text-white hover:bg-white/20 text-xs"
                onClick={openPolicyEditor}
              >
                Edit
              </button>
              <Pill tone={settings?.sendsEnabled ? 'success' : 'warning'}>{settings?.sendsEnabled ? 'On' : 'Off'}</Pill>
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm text-white/80">
            <div>
              <div className="text-white/60">Per tx limit</div>
              <div className="font-mono">{perTxLimitPixe ? `${perTxLimitPixe} PIXE` : '—'}</div>
            </div>
            <div>
              <div className="text-white/60">Per day limit</div>
              <div className="font-mono">{perDayLimitPixe ? `${perDayLimitPixe} PIXE` : '—'}</div>
            </div>
            <div>
              <div className="text-white/60">Daily remaining</div>
              <div className="font-mono">{dailyRemainingPixe ? `${dailyRemainingPixe} PIXE` : '—'}</div>
            </div>
            <div>
              <div className="text-white/60">Min treasury balance</div>
              <div className="font-mono">{minTreasuryBalancePixe ? `${minTreasuryBalancePixe} PIXE` : '—'}</div>
            </div>
            <div>
              <div className="text-white/60">Confirmations</div>
              <div>{settings?.confirmationsRequired || FALLBACK_SUMMARY.settings?.confirmationsRequired || 0} blocks</div>
            </div>
            <div>
              <div className="text-white/60">Circuit breaker</div>
              <div className="font-mono">{settings?.circuitTripped ? 'TRIPPED' : 'Normal'}</div>
            </div>
            <div>
              <div className="text-white/60">Vault moves</div>
              <div className="font-mono">{settings?.vaultMovesEnabled ? 'Enabled' : 'Disabled'}</div>
            </div>
          </dl>
          {settings?.confirmPhrase && (
            <div className="text-xs text-white/60">
              Confirmation phrase required: <span className="font-mono text-white">{settings.confirmPhrase}</span>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-white/70">Reconciliation</div>
              <div className="text-xl font-bold text-white">Ledger vs chain</div>
              <div className="text-sm text-white/60">Checks house + user wallets against on-chain balances (basis: {reconciliation?.basis || 'spot'}).</div>
            </div>
            <Pill tone={reconTone}>
              {reconciliation?.status === 'failed' ? 'Failed' : reconciliation?.mismatches ? `${reconciliation.mismatches} issues` : 'Clean'}
            </Pill>
          </div>
          <div className="text-sm text-white/70">
            Last run: {reconciliation?.lastRunAt ? new Date(reconciliation.lastRunAt).toLocaleString() : '—'}
          </div>
          <button
            className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
            onClick={() => {
              setMessage('Reconciliation running…')
              runTreasuryReconciliation()
                .then((res) => {
                  setMessage(`Reconciliation ${res.status} (${res.mismatches} mismatches)`)
                  refreshAll()
                })
                .catch(() => setMessage('Reconciliation failed; check logs'))
            }}
          >
            Run reconciliation
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-white/70">Key Management</div>
            <div className="text-xl font-bold text-white">Service master key (SMK)</div>
            <div className="text-sm text-white/60">Stored in K8s secret; rotate via plan and verify.</div>
          </div>
          <Pill tone={smkStatus?.error ? 'danger' : smkStatus?.present ? 'success' : 'warning'}>
            {smkStatus?.error ? 'Needs attention' : smkStatus?.present ? 'Configured' : 'Missing'}
          </Pill>
        </div>
        {!smkStatus && <div className="text-sm text-white/70">SMK status unavailable.</div>}
        {smkStatus && (
          <dl className="grid grid-cols-2 gap-3 text-sm text-white/80">
            <div>
              <div className="text-white/60">Present</div>
              <div>{smkStatus.present ? 'Yes' : 'No'}</div>
            </div>
            <div>
              <div className="text-white/60">Base64 valid</div>
              <div>{smkStatus.base64Decodes ? 'Yes' : 'No'}</div>
            </div>
            <div>
              <div className="text-white/60">Decrypt sample</div>
              <div>{smkStatus.sampleChecked ? (smkStatus.canDecryptSample ? 'OK' : 'Failed') : 'Not checked'}</div>
            </div>
            <div>
              <div className="text-white/60">Old key present</div>
              <div>{smkStatus.hasOldKey ? (smkStatus.oldKeyDecodes ? 'Yes' : 'Invalid') : 'No'}</div>
            </div>
            <div>
              <div className="text-white/60">Key version</div>
              <div>{smkStatus.currentKeyVersion ?? 'Unknown'}</div>
            </div>
            <div>
              <div className="text-white/60">Key fingerprint</div>
              <div className="font-mono">{smkStatus.fingerprint || 'Unavailable'}</div>
            </div>
            {smkStatus.error && (
              <div className="col-span-2 text-rose-200 text-xs font-mono">Error: {smkStatus.error}</div>
            )}
          </dl>
        )}
        {smkLastChecked && (
          <div className="text-xs text-white/50">Last checked: {new Date(smkLastChecked).toLocaleString()}</div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
            onClick={() => {
              setSmkMessage(null)
              createSmkRotationPlan()
                .then((plan) => setSmkPlan(plan))
                .catch((err) => setSmkMessage(err?.response?.data?.detail || 'Failed to generate rotation plan'))
                .finally(() => refreshSmkStatus())
            }}
          >
            Generate rotation plan
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
            onClick={() => {
              setSmkMessage('Verifying SMK...')
              verifySmk()
                .then((res) => {
                  setSmkStatus(res)
                  setSmkMessage('SMK verified')
                  setSmkLastChecked(new Date().toISOString())
                })
                .catch(() => setSmkMessage('SMK verify failed'))
            }}
          >
            Verify after patch
          </button>
          <div className="flex items-center gap-2">
            <input
              className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white text-xs"
              placeholder="Type REWRAP"
              value={rewrapConfirm}
              onChange={(e) => setRewrapConfirm(e.target.value)}
            />
            <button
              className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-50"
              disabled={rewrapConfirm.trim() !== 'REWRAP' || !smkStatus?.present}
              onClick={() => {
                setSmkMessage('Rewrapping secrets...')
                rewrapSmk()
                  .then((res) => {
                    setRewrapConfirm('')
                    setSmkMessage(`Rewrapped ${res.updated} of ${res.processed} (failed ${res.failedIdsCount})`)
                    refreshSmkStatus()
                  })
                  .catch(() => setSmkMessage('Rewrap failed'))
              }}
            >
              Rewrap secrets
            </button>
          </div>
        </div>
        {smkMessage && <div className="text-xs text-white/70">{smkMessage}</div>}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-white/70">House accounts</div>
            <div className="text-xl font-bold text-white">Custody & pools</div>
          </div>
          <button
            className={`px-3 py-1 rounded-lg text-xs border ${
              showAdvanced
                ? 'bg-white/10 text-white border-white/20'
                : 'bg-white/5 text-white/60 border-white/10 hover:text-white'
            }`}
            onClick={() => setShowAdvanced((v) => !v)}
          >
            Advanced
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-white/80">
            <thead>
              <tr className="text-left text-white/60">
                <th className="py-2">Account</th>
                <th className="py-2">Balances</th>
                <th className="py-2 text-center">Role</th>
                <th className="py-2 text-center">Status</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {visibleAccounts.map((acct) => {
                const reasons = acct.sendDisabledReasons && acct.sendDisabledReasons.length ? acct.sendDisabledReasons : []
                const showSend = acct.key === 'sell_hot'
                const showImport = acct.key === 'sell_hot'
                const showRefill = acct.key === 'sale_pool'
                const showCreate = (acct.key === 'sell_hot' || acct.key === 'fees') && !acct.ss58
                const sendBlocked =
                  settings?.sendsEnabled === false || !acct.ss58 || acct.canSend === false || acct.hasKey === false || reasons.length > 0
                const sendReason = reasons.length
                  ? reasons.map((r) => reasonLabels[r] || r).join(', ')
                  : !acct.ss58
                  ? 'Missing address'
                  : settings?.sendsEnabled === false
                  ? 'Disabled by policy'
                  : acct.canSend === false || acct.hasKey === false
                  ? 'Missing key / watch-only'
                  : ''
                const balanceSourceLabel = acct.balanceSource === 'chain' ? 'Chain' : 'Ledger'
                return (
                  <tr key={acct.key}>
                    <td className="py-2">
                      <div className="min-w-0 font-semibold text-white truncate">{displayAccountLabel(acct.key, acct.label)}</div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {acct.ss58 ? (
                            <>
                              <span className="font-mono text-xs text-white/70 whitespace-nowrap" title={acct.ss58}>
                                {formatSs58Short(acct.ss58)}
                              </span>
                              <button
                                className="px-2 py-0.5 rounded bg-white/10 text-white hover:bg-white/20 text-xs"
                                onClick={() => copyToClipboard(acct.ss58)}
                                title="Copy address"
                              >
                                Copy
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-white/50">Not configured</span>
                          )}
                        </div>
                        {showAdvanced && (
                          <span className="text-xs text-white/50 whitespace-nowrap">({acct.key})</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2">
                      {acct.availablePixe === null && acct.heldPixe === null && acct.totalPixe === null && acct.balancePixe === null ? (
                        <div className="space-y-1 text-sm text-white/50">
                          <div>—</div>
                          <div className="text-[10px] text-white/50">Source: {balanceSourceLabel}</div>
                        </div>
                      ) : (
                        <div className="space-y-1 text-xs text-white/80">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-white/60">Available</span>
                            <span className="font-mono">{renderPixeCell(acct.availablePixe ?? acct.balancePixe)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-white/60">Held</span>
                            <span className="font-mono">{renderPixeCell(acct.heldPixe)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-white/60">Total</span>
                            <span className="font-mono">{renderPixeCell(acct.totalPixe ?? acct.balancePixe)}</span>
                          </div>
                          <div className="text-[10px] text-white/50">Source: {balanceSourceLabel}</div>
                        </div>
                      )}
                    </td>
                    <td className="py-2 text-center">
                      <Pill tone={acct.role === 'hot' ? 'success' : acct.role === 'watch' ? 'neutral' : acct.role === 'cold' ? 'warning' : 'warning'}>
                        {acct.role === 'hot' ? 'Hot' : acct.role === 'watch' ? 'Watch-only' : acct.role === 'cold' ? 'Cold' : 'Missing'}
                      </Pill>
                    </td>
                    <td className="py-2 text-center">
                      <Pill tone={acct.status === 'configured' ? 'success' : acct.status === 'watch-only' ? 'neutral' : 'warning'}>
                        {acct.status}
                      </Pill>
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            className="px-3 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500"
                            onClick={() => doReceive(acct.key)}
                            disabled={!acct.ss58}
                            title={acct.ss58 ? '' : 'Missing address'}
                          >
                            Receive
                          </button>
                          {showSend && (
                            <button
                              className="px-3 py-1 rounded-lg bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-50"
                              disabled={sendBlocked}
                              title={sendReason}
                              onClick={() =>
                                setSendState((s) => ({
                                  ...s,
                                  open: true,
                                  houseKey: acct.key,
                                  dest: s.dest || me?.ss58_address || '',
                                  confirm: '',
                                }))
                              }
                            >
                              Send
                            </button>
                          )}
                        </div>
                        {(showRefill || showCreate || (showImport && ['missing', 'missing-key'].includes(acct.status))) && (
                          <div className="flex flex-wrap justify-end gap-2">
                            {showRefill && (
                              <button
                                className="px-3 py-1 rounded-lg bg-amber-600 text-black hover:bg-amber-500 disabled:opacity-50"
                                disabled={!canRefill}
                                title={canRefill ? '' : refillReason}
                                onClick={() =>
                                  setRefillState((s) => ({
                                    ...s,
                                    open: true,
                                    confirm: '',
                                    vaultMnemonic: '',
                                    vaultAck: false,
                                    revealMnemonic: false,
                                  }))
                                }
                              >
                                Refill sell hot
                              </button>
                            )}
                            {showCreate && (
                              <button
                                className="px-3 py-1 rounded-lg bg-amber-600 text-black hover:bg-amber-500"
                                onClick={() =>
                                  setCreateState({
                                    houseKey: acct.key,
                                    open: true,
                                    revealOnce: false,
                                    revealAck: false,
                                    confirmPhrase: '',
                                  })
                                }
                              >
                                Create wallet
                              </button>
                            )}
                            {showImport && ['missing', 'missing-key'].includes(acct.status) && (
                              <button
                                className="px-3 py-1 rounded-lg bg-amber-600 text-black hover:bg-amber-500"
                                onClick={() => setImportState({ houseKey: acct.key, mnemonic: '', open: true, ackLargeBalanceRisk: false })}
                              >
                                Import key
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-white/70">Activity</div>
            <div className="text-xl font-bold text-white">Recent treasury actions</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-white/80">
            <thead>
              <tr className="text-left text-white/60">
                <th className="py-2">Time</th>
                <th className="py-2">Type</th>
                <th className="py-2">From</th>
                <th className="py-2">To</th>
                <th className="py-2">Amount</th>
                <th className="py-2">Status</th>
                <th className="py-2">Block</th>
                <th className="py-2">TxHash</th>
              </tr>
            </thead>
              <tbody className="divide-y divide-white/5">
                {(activity.length ? activity : FALLBACK_ACTIVITY).map((evt) => {
                  const txId = parseTreasuryTxId(evt.id)
                  const failureLabel = formatTxFailure(evt.submitErrorCode)
                  return (
                    <tr key={evt.id}>
                      <td className="py-2">{new Date(evt.ts).toLocaleString()}</td>
                      <td className="py-2 capitalize">{evt.type}</td>
                      <td className="py-2">{formatActivityParty(evt.from, summary?.houseAccounts)}</td>
                      <td className="py-2">{formatActivityParty(evt.to, summary?.houseAccounts)}</td>
                      <td className="py-2">{evt.amountPixe} PIXE</td>
                      <td className="py-2">
                        <Pill
                          tone={
                            evt.status === 'failed'
                              ? 'danger'
                              : evt.status === 'submitted' || evt.status === 'pending'
                              ? 'warning'
                              : evt.status === 'in_block' || evt.status === 'finalized'
                              ? 'success'
                              : 'neutral'
                          }
                        >
                          {evt.status}
                        </Pill>
                        {failureLabel && (
                          <div className="text-xs text-white/50 mt-1">{failureLabel}</div>
                        )}
                      </td>
                      <td className="py-2">{evt.blockNumber ?? '—'}</td>
                      <td className="py-2 font-mono text-xs">
                        {evt.txHash ? (
                          <div className="flex items-center gap-2">
                            <span>{shortHash(evt.txHash)}</span>
                            <button
                              className="px-2 py-0.5 rounded bg-white/10 text-white hover:bg-white/20"
                              onClick={() => navigator.clipboard?.writeText(evt.txHash || '')}
                            >
                              Copy
                            </button>
                            {txId && (
                              <button
                                className="px-2 py-0.5 rounded bg-white/10 text-white hover:bg-white/20"
                                onClick={() =>
                                  refreshTreasuryTx(txId)
                                    .then((res) => {
                                      const reasonLabel = formatTxFailure(res.reason || res.submitErrorCode)
                                      setMessage(`Checked: ${res.status}${reasonLabel ? ` (${reasonLabel})` : ''}`)
                                      return refreshAll()
                                    })
                                    .catch(() => setMessage('Status refresh failed'))
                                }
                              >
                                Check
                              </button>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  )
}
